import './BatterSingleSessionBattingStatChart.scss';

import { useMemo } from 'react';

import { Typography } from '@trackman/web-shared-components';

import { PlayerStatChartConfigDto, BatterSingleSessionBattingStatChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useGetUnitSystem } from 'hooks/helperHooks';

const BatterSingleSessionBattingStatChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<BatterSingleSessionBattingStatChartDataDto, PlayerStatChartConfigDto>) => {
  const unitSystem = useGetUnitSystem();

  const value = useMemo(() => {
    if (chartData?.battingStatValue == null || isLoading) {
      return '-';
    }

    switch (extraChartData.playerStat) {
      case 'ExitSpeed':
      case 'MaxExitSpeed':
      case 'LaunchAngle':
      case 'SwingPercent':
      case 'ContactPercent':
      case 'ChasePercent':
      case 'WhiffPercent':
      case 'FlyBallPercent':
      case 'GroundBallPercent':
        return chartData.battingStatValue.toFixed(1);
      case 'BattingAverage':
        return chartData.battingStatValueString ?? '-';
      case 'OnBasePercent':
      case 'Slugging':
      case 'OnBasePlusSlugging':
        return chartData.battingStatValue.toFixed(3).replace(/^0+/, '');
      default:
        return chartData.battingStatValue;
    }
  }, [isLoading, chartData, extraChartData?.playerStat]);

  const unit = useMemo(() => {
    switch (extraChartData.playerStat) {
      case 'ExitSpeed':
      case 'MaxExitSpeed':
        return unitSystem === 'Metric' ? 'kph' : 'mph';
      case 'LaunchAngle':
        return 'deg';
      case 'SwingPercent':
      case 'ContactPercent':
      case 'ChasePercent':
      case 'WhiffPercent':
      case 'FlyBallPercent':
      case 'GroundBallPercent':
        return '%';
      case 'BattingAverage':
        return chartData?.battingStatValue != null ? `(${chartData.battingStatValue.toFixed(3).replace(/^0+/, '')})` : '';
      default:
        return '';
    }
  }, [chartData?.battingStatValue, extraChartData?.playerStat]);

  return (
    <div className='BatterSingleSessionBattingStatChart-container'>
      <div className='BatterSingleSessionBattingStatChart-value-wrapper'>
        <Typography variant='h1' component='div' className='BatterSingleSessionBattingStatChart-value-label'>
          {value}
        </Typography>
        {unit && (
          <Typography variant='body' component='div' className='BatterSingleSessionBattingStatChart-unit-label'>
            {unit}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default BatterSingleSessionBattingStatChart;
